import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Observer, observer } from 'mobx-react';
import { computed, observable } from 'mobx';

import {
  DefaultButton,
  PrimaryButton,
} from 'office-ui-fabric-react/lib/Button';
import { Panel, PanelType } from 'office-ui-fabric-react/lib/Panel';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import {
  MessageBar,
  MessageBarType,
} from 'office-ui-fabric-react/lib/MessageBar';

import { localizable } from 'components/Localization';
import { FooterContainer } from 'components/PanelFooter';

const config =
  require(`../../../@${process.env.REACT_APP_TENANT}/config.js`).default;

@localizable
@observer
class MessagingPanel extends Component {
  static propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
  };

  @observable audience = 'everyone';
  @observable template = 'cust_message';
  @observable subject = '';
  @observable text = '';

  @observable sending = false;
  @observable result = null;
  @observable error = null;

  handleChange = (field) => (ev, value) => {
    this[field] = value;
  };

  handleChangeOptionKey = (field) => (ev, option, index, value) => {
    if (option) this[field] = option.key;
    else this[field] = value;
  };

  @computed get isValid() {
    return (
      (this.isSectionDisabled('subject') || !!this.subject) &&
      (this.isSectionDisabled('text') || !!this.text)
    );
  }

  isSectionDisabled = (section) => {
    return !config.messagingTemplates[this.template].includes(section);
  };

  handleSend = async () => {
    const { sale, t } = this.props;
    this.sending = true;
    try {
      this.result = await sale.sendNotification({
        audience: this.audience,
        templateName: this.template,
        data: {
          subject: this.subject,
          text: this.text,
        },
      });
    } catch (error) {
      this.error = t('Failed to send the message');
    } finally {
      this.sending = false;
    }
  };

  handleClose = () => {
    const { onClose } = this.props;
    if (onClose) {
      onClose();
    }
  };

  render() {
    const { t } = this.props;

    return (
      <Panel
        headerText={t('Messaging')}
        type={PanelType.medium}
        isOpen={this.props.isOpen}
        onDismissed={this.handleClose}
        onRenderFooterContent={this.renderPanelFooterContent}
        isFooterAtBottom={true}
        closeButtonAriaLabel={t('Close')}
      >
        <div className="ms-Grid">
          {this.result === null && (
            <Fragment>
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <Dropdown
                    label={t('Template')}
                    name="template"
                    selectedKey={this.template}
                    onChange={this.handleChangeOptionKey('template')}
                    options={Object.keys(config.messagingTemplates).map(
                      (key) => {
                        return { key: key, text: t(`template::${key}`) };
                      }
                    )}
                    required={true}
                    disabled={this.sending}
                  />
                </div>
              </div>
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <Dropdown
                    label={t('Audience')}
                    name="audience"
                    selectedKey={this.audience}
                    onChange={this.handleChangeOptionKey('audience')}
                    options={config.messagingAudienceTypes.map((key) => {
                      return { key: key, text: t(`audiencetype::${key}`) };
                    })}
                    required={true}
                    disabled={this.sending}
                  />
                </div>
              </div>
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <TextField
                    label={t('Subject')}
                    name="subject"
                    value={this.subject}
                    onChange={this.handleChange('subject')}
                    required={true}
                    disabled={this.sending || this.isSectionDisabled('subject')}
                  />
                </div>
              </div>
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <TextField
                    label={t('Text')}
                    multiline
                    name="text"
                    value={this.text}
                    onChange={this.handleChange('text')}
                    required={true}
                    disabled={this.sending || this.isSectionDisabled('text')}
                  />
                </div>
              </div>
              {this.error && (
                <div className="ms-Grid-row" style={{ paddingTop: '15px' }}>
                  <div className="ms-Grid-col ms-sm12">
                    <MessageBar messageBarType={MessageBarType.error}>
                      <div>{this.error}</div>
                    </MessageBar>
                  </div>
                </div>
              )}
            </Fragment>
          )}
          {this.result && (
            <Fragment>
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  {this.result.succeeded.length > 0 && (
                    <MessageBar messageBarType={MessageBarType.success}>
                      <div>
                        {t('Number of successfully sent messages')}:
                        {this.result.succeeded.length}
                      </div>
                    </MessageBar>
                  )}
                  {this.result.failed.length > 0 && (
                    <MessageBar messageBarType={MessageBarType.error}>
                      <div>
                        {t('Number of failed messages')}:
                        {this.result.failed.length}
                      </div>
                    </MessageBar>
                  )}
                  {this.result.succeeded.length === 0 &&
                    this.result.failed.length === 0 && (
                      <MessageBar messageBarType={MessageBarType.info}>
                        <div>{t('No message has been sent')}</div>
                      </MessageBar>
                    )}
                </div>
              </div>
            </Fragment>
          )}
        </div>
      </Panel>
    );
  }

  renderPanelFooterContent = () => {
    const { t } = this.props;
    return (
      <Observer
        render={() => (
          <FooterContainer>
            <PrimaryButton
              onClick={this.handleSend}
              disabled={!this.isValid || this.sending || this.result}
            >
              {this.sending ? `${t('Sending')}...` : t('Send')}
              {this.sending && (
                <Spinner
                  size={SpinnerSize.xSmall}
                  style={{ paddingLeft: '10px' }}
                />
              )}
            </PrimaryButton>
            <DefaultButton onClick={this.handleClose}>
              {t('Close')}
            </DefaultButton>
          </FooterContainer>
        )}
      />
    );
  };
}

export default MessagingPanel;



// WEBPACK FOOTER //
// ./src/app/containers/Sale/SaleMessagingSection/MessagingPanel.jsx